// ── How a page moves while it turns ─────────────────────────────────────────
// A transition draws the page being read and the neighbouring page (see
// usePageTurn). It is handed the elements as one object:
//   page       the page being read, in the document
//   incoming   the neighbouring page's drawing, inside the cover (fixed over
//              the page window, above the page)
// and one number, `offset`: how far, in px, the finger (or the animation) has
// pulled the page sideways (negative = pulled left, towards the next page).
// `dir` is +1 when the incoming page is the next one, -1 when it is the
// previous one, 0 when there is none (the drag only gives a little).
//
// Every transition has the same methods, so the engine never knows which one
// it is running:
//   initial(dir, width)  inline styles for the layers as they first render,
//       so the incoming page is never seen in the wrong place for a frame
//   frame({ …elements, dir, offset, width })   draw one moment (drag)
//   animate({ …, from, to, duration, onDone })  run from → to, then call
//       onDone exactly once; returns { finish(), cancel() }: finish jumps to
//       the end and calls onDone straight away (the next turn arrived early),
//       cancel just stops (the page is going away)
//   turnDuration(step)   optional: how long a turn from a key or button runs
//   movesPage            whether `page` itself moves (and so wants
//       will-change while it does)
// Only transform and opacity are touched, so nothing re-lays out mid-turn.

const r1 = n => Math.round(n * 10) / 10;
const tx = px => `translate3d(${r1(px)}px,0,0)`;
const EASE = 'cubic-bezier(0.22, 0.72, 0.24, 1)';

// ── Slide ───────────────────────────────────────────────────────────────────
// The page and its neighbour move side by side, like a strip of film.

function slideFrame({ page, incoming, dir, offset, width }) {
  if (page) page.style.transform = offset ? tx(offset) : '';
  if (incoming && dir) incoming.style.transform = tx(dir * width + offset);
}

function slideAnimate({ page, incoming, dir, from, to, width, duration, onDone }) {
  let done = false;
  const anims = [];
  const stop = () => anims.forEach(a => { a.onfinish = null; a.cancel(); });
  const end = () => {
    if (done) return;
    done = true;
    stop();
    onDone?.();
  };
  const cancel = () => { if (!done) { done = true; stop(); } };
  // The resting style is set first; the animation plays over it and, once
  // cancelled or finished, leaves the element exactly where it should be.
  slideFrame({ page, incoming, dir, offset: to, width });
  if (!page?.animate || duration <= 0 || from === to) {
    end();
    return { finish: end, cancel };
  }
  const opts = { duration, easing: EASE };
  anims.push(page.animate([{ transform: tx(from) }, { transform: tx(to) }], opts));
  if (incoming && dir) {
    anims.push(incoming.animate(
      [{ transform: tx(dir * width + from) }, { transform: tx(dir * width + to) }], opts));
  }
  anims[0].onfinish = end;
  return { finish: end, cancel };
}

export const slide = {
  name: 'slide',
  movesPage: true,
  initial: dir => ({ incoming: { transform: `translate3d(${dir * 100}%,0,0)` } }),
  frame: slideFrame,
  animate: slideAnimate,
};

// A page-curl transition lives on branch curl-wip; add it here to bring it back.
const TRANSITIONS = { slide };

export function getTransition(name) {
  return TRANSITIONS[name] || slide;
}
